import React from 'react';
import DownloadsPageLayout, {
  DownloadsTab
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/DownloadsPageLayout';
import { NewSettingsLayoutProps } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/NewSettingsLayout';
import ProfileContainerLayout from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Profile/ProfileContainerLayout';
import { getAppSubscriptionTier } from 'src/utils/app';
import { useAppSelector } from 'src/utils/Hooks';
import UserTeamManagement from 'src/routes/UserAccount/UserTeamManagement/UserTeamManagement';
import SettingsIntegrationsPageLayout from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Integrations/SettingsIntegrationsPageLayout';
import BulkAdjustmentUploadsPageLayout from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Adjustments/BulkAdjustmentUploadsPageLayout';
import useNotificationsBadge from 'src/utils/Hooks/useNotificationsBadge';
import { SidebarLabel } from 'src/components/BeaconV2Components/GenericSidebarNav/GenericSidebar';
import SettingsContentPageLayout from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/SettingsContentPageLayout';
import BeaconAlertsPageLayout from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/beacon/BeaconAlertsPageLayout';
import { NotificationBadge } from 'src/components/common/NotificationBadge';
import { showBeaconAlertV1 } from 'src/utils/app';

export enum BEACON_SETTINGS_TABS {
  PROFILE = 'profile',
  DOWNLOADS = 'downloads',
  TEAM = 'team',
  INTEGRATIONS = 'integrations'
}

export enum BEACON_SETTINGS_SUBTABS {
  PROFILE = 'profile',
  SECURITY = 'security'
}

export enum BEACON_SETTINGS_LABELS {
  PROFILE = 'Profile',
  SUMMARY = 'Summary',
  SECURITY = 'Security',
  DOWNLOADS = 'Downloads',
  TEAM = 'Team',
  INTEGRATIONS = 'Integrations',
  ADJUSTMENTS = 'Adjustments',
  CONTENT = 'Content',
  ALERTS = 'Alerts',
  PRESENTATIONS = 'Presentations'
}

export const useSettingsRoutes = (props: NewSettingsLayoutProps) => {
  const { queryParams } = useAppSelector((state) => state.app);
  const { searchParams } = queryParams;

  const shouldShowNotificationBadge = useNotificationsBadge(
    (state) =>
      state.notificationBadges.presentations || state.notificationBadges.export || state.notificationBadges.adjustments
  );
  const showAlert = showBeaconAlertV1();
  const {
    user,
    handleProfileChange,
    onSubmit,
    handlePhoneNumberChange,
    handleNewPasswordChange,
    handlePasswordConfirmationChange,
    profile
  } = props;
  const subscriptionTier = getAppSubscriptionTier();
  const notProUser = subscriptionTier !== 'pro';

  const tabs = [
    {
      label: BEACON_SETTINGS_LABELS.PROFILE,
      route: `/account/profile${searchParams}`,
      index: 0,
      renderComponent: (
        <ProfileContainerLayout
          user={user}
          handleProfileChange={handleProfileChange}
          onSubmit={onSubmit}
          handlePhoneNumberChange={handlePhoneNumberChange}
          handleNewPasswordChange={handleNewPasswordChange}
          handlePasswordConfirmationChange={handlePasswordConfirmationChange}
          profile={profile}
        />
      )
    },
    {
      label: (
        <div>
          {BEACON_SETTINGS_LABELS.DOWNLOADS}
          {shouldShowNotificationBadge && <NotificationBadge />}
        </div>
      ),
      route: `/account/downloads${searchParams}&tab=${DownloadsTab.ExportsDownload}`,
      index: 1,
      renderComponent: <DownloadsPageLayout />
    },
    {
      label: BEACON_SETTINGS_LABELS.TEAM,
      oldBeaconPath: 'account',
      oldBeaconEntity: 'profile',
      route: `/account/team${searchParams}`,
      index: 2,
      renderComponent: <UserTeamManagement />
    },
    {
      label: BEACON_SETTINGS_LABELS.INTEGRATIONS,
      route: `/account/integrations${searchParams}`,
      index: 3,
      renderComponent: <SettingsIntegrationsPageLayout />
    },
    {
      label: notProUser ? (
        <SidebarLabel requireSubscription={notProUser} label={BEACON_SETTINGS_LABELS.ADJUSTMENTS} />
      ) : (
        BEACON_SETTINGS_LABELS.ADJUSTMENTS
      ),
      route: `/account/adjustments${searchParams}`,
      index: 4,
      renderComponent: <BulkAdjustmentUploadsPageLayout />,
      requireSubscription: notProUser
    },
    {
      label: BEACON_SETTINGS_LABELS.CONTENT,
      route: `/account/content/settings${searchParams}`,
      index: 5,
      renderComponent: <SettingsContentPageLayout />
    },
    {
      label: BEACON_SETTINGS_LABELS.CONTENT,
      route: `/account/content/credits${searchParams}`,
      index: 5,
      hidden: true,
      renderComponent: <SettingsContentPageLayout />
    },
    ...(showAlert
      ? [
          {
            label: BEACON_SETTINGS_LABELS.ALERTS,
            route: `/account/alerts${searchParams}`,
            index: 6,
            renderComponent: <BeaconAlertsPageLayout />
          },
          {
            label: BEACON_SETTINGS_LABELS.ALERTS,
            route: `/account/alerts/team${searchParams}`,
            index: 6,
            hidden: true,
            renderComponent: <BeaconAlertsPageLayout />
          },
          {
            label: BEACON_SETTINGS_LABELS.ALERTS,
            route: `/account/alerts/template${searchParams}`,
            index: 6,
            hidden: true,
            renderComponent: <BeaconAlertsPageLayout />
          },
          {
            label: BEACON_SETTINGS_LABELS.ALERTS,
            route: `/account/alerts/create${searchParams}`,
            index: 6,
            hidden: true,
            renderComponent: <BeaconAlertsPageLayout />
          },
          {
            label: BEACON_SETTINGS_LABELS.ALERTS,
            route: `/account/alerts/edit${searchParams}`,
            index: 6,
            hidden: true,
            renderComponent: <BeaconAlertsPageLayout />
          }
        ]
      : []),
    {
      label: BEACON_SETTINGS_LABELS.CONTENT,
      route: `/account/content/settings${searchParams}`,
      index: 5,
      hidden: true
    }
  ];

  return tabs;
};
